import { type Clip, type Op, type Timeline, keyframeAt, snapToFrame, sortedClips, teileClip, transformAt } from '@schnitt/shared';
import { newId } from './ids';

/**
 * The edits the toolbar offers on a selected clip, as pure functions from the
 * cut to a list of operations. Keeping them out of the components means the
 * toolbar and a keyboard shortcut cannot drift apart, and nothing here needs
 * a DOM to be reasoned about.
 */

/**
 * Cut every clip under the playhead in two -- or only the selected ones, when
 * there is a selection. A clip is only split where both halves keep at least
 * one frame, otherwise "split" would silently produce an empty clip.
 */
export function splitAtPlayhead(timeline: Timeline, playhead: number, selection: string[]): Op[] {
  return splitAt(timeline, playhead, selection);
}

/** The same cut at any moment -- the context menu splits where the finger is. */
export function splitAt(timeline: Timeline, seconds: number, selection: string[]): Op[] {
  const fps = timeline.settings.fps;
  const t = snapToFrame(seconds, fps);
  const ops: Op[] = [];
  for (const track of timeline.tracks) {
    for (const clip of sortedClips(track)) {
      if (t <= clip.start + 1 / fps || t >= clip.start + clip.duration - 1 / fps) continue;
      if (selection.length > 0 && !selection.includes(clip.id)) continue;
      // Wo die Quelle weitergeht und wie schnell, rechnet `teileClip` -- mit
      // Rampe ist das nicht mehr Länge mal Tempo (S6).
      const { links, rechts } = teileClip(clip, t - clip.start);
      ops.push({ op: 'updateClip', clipId: clip.id, patch: links });
      ops.push({
        op: 'addClip',
        trackId: track.id,
        clip: {
          ...structuredClone(clip),
          id: newId('c'),
          ...rechts,
          fadeIn: 0,
          transitionIn: null,
        },
      });
    }
  }
  return ops;
}

/**
 * A copy of the clip on the same track, in the first gap after it that is
 * long enough -- right behind it when that space is free, otherwise at the
 * end of the track. Never on top of another clip: on the base track two
 * clips in the same place would fight over the picture.
 */
export function duplicateClip(timeline: Timeline, clipId: string): { op: Op; id: string } | null {
  for (const track of timeline.tracks) {
    const clip = track.clips.find((c) => c.id === clipId);
    if (!clip) continue;
    const start = nextFreeStart(timeline, track.id, clip.start + clip.duration, clip.duration);
    const id = newId('c');
    // Ohne Gruppe (S3): eine Kopie ist ein neues Ding und gehört nicht
    // stillschweigend zu dem, wozu das Original gehörte -- sonst zöge sie
    // beim nächsten Griff an einem Gruppenclip mit.
    const copy: Clip = { ...structuredClone(clip), id, start, transitionIn: null, gruppe: null };
    return { op: { op: 'addClip', trackId: track.id, clip: copy }, id };
  }
  return null;
}

/** The first gap on a track that fits a clip of `duration`, or the end of the track. */
export function nextFreeStart(timeline: Timeline, trackId: string, from: number, duration: number): number {
  const track = timeline.tracks.find((t) => t.id === trackId);
  if (!track) return from;
  let start = from;
  for (const other of sortedClips(track)) {
    const otherEnd = other.start + other.duration;
    if (otherEnd <= start) continue;
    if (other.start >= start + duration) break;
    start = otherEnd;
  }
  return start;
}

/**
 * Paste copied clips so that the earliest of them starts at `at`, keeping
 * their spacing and their tracks. If that spot on a track is taken, the whole
 * group slides to the next free start of its first clip -- pasted clips never
 * land on top of what is there. New ids throughout: a copy is a new thing.
 */
export function pasteClips(
  timeline: Timeline,
  entries: { clip: Clip; trackId: string }[],
  at: number,
): { ops: Op[]; ids: string[] } {
  if (entries.length === 0) return { ops: [], ids: [] };
  const origin = Math.min(...entries.map((e) => e.clip.start));
  const first = [...entries].sort((a, b) => a.clip.start - b.clip.start)[0]!;
  // The track it was copied from, if it still exists; the base track otherwise.
  const fallback = timeline.tracks.find((t) => t.kind === 'video')?.id;
  const trackFor = (id: string) => (timeline.tracks.some((t) => t.id === id) ? id : fallback);
  const firstTrack = trackFor(first.trackId);
  if (!firstTrack) return { ops: [], ids: [] };
  const shift = nextFreeStart(timeline, firstTrack, at, first.clip.duration) - at;
  const ops: Op[] = [];
  const ids: string[] = [];
  // Eingefügte Gruppen bleiben Gruppen, aber eigene (S3): jede alte Kennung
  // bekommt genau eine neue. Behielte die Kopie die alte, hinge sie am
  // Original -- ein Zug am einen zöge am anderen.
  const neueGruppen = new Map<string, string>();
  for (const { clip, trackId: from } of entries) {
    const trackId = trackFor(from) ?? firstTrack;
    const id = newId('c');
    ids.push(id);
    let gruppe: string | null = null;
    if (clip.gruppe) {
      gruppe = neueGruppen.get(clip.gruppe) ?? newId('g');
      neueGruppen.set(clip.gruppe, gruppe);
    }
    ops.push({
      op: 'addClip',
      trackId,
      clip: { ...structuredClone(clip), id, start: Math.max(0, clip.start - origin + at + shift), transitionIn: null, gruppe },
    });
  }
  return { ops, ids };
}


/**
 * Setzt einen Keyframe am Abspielkopf -- oder nimmt ihn weg, wenn dort schon
 * einer steht. Ein Knopf, zwei Richtungen: so muss man sich nicht merken, ob
 * an dieser Stelle schon etwas liegt.
 */
export function toggleKeyframe(clip: Clip, local: number, fps: number): { op: Op; gesetzt: boolean } {
  const vorhanden = keyframeAt(clip, local, 1 / fps);
  const keyframes =
    vorhanden !== null
      ? clip.keyframes.filter((k) => k.at !== vorhanden)
      : [...clip.keyframes, { at: Math.max(0, local), transform: transformAt(clip, local) }];
  return { op: { op: 'updateClip', clipId: clip.id, patch: { keyframes } } as Op, gesetzt: vorhanden === null };
}

/**
 * Die Pausen aus einem Clip schneiden (7.66a): aus einem Clip werden so
 * viele, wie Stücke bleiben. Das erste behält die Kennung und alles, was
 * daran hängt; die weiteren sind Abschriften ohne Einblendung und ohne
 * Übergang -- ein Schnitt mitten im Satz blendet nicht.
 */
export function pausenRaus(timeline: Timeline, clipId: string, stuecke: { start: number; duration: number; sourceIn: number }[]): Op[] {
  const track = timeline.tracks.find((t) => t.clips.some((c) => c.id === clipId));
  const clip = track?.clips.find((c) => c.id === clipId);
  if (!track || !clip || stuecke.length === 0) return [];
  const [erstes, ...weitere] = stuecke;
  const ops: Op[] = [{ op: 'updateClip', clipId, patch: { ...erstes!, fadeOut: weitere.length ? 0 : clip.fadeOut } }];
  weitere.forEach((stueck, i) => {
    ops.push({
      op: 'addClip',
      trackId: track.id,
      clip: {
        ...structuredClone(clip),
        id: newId('c'),
        ...stueck,
        fadeIn: 0,
        fadeOut: i === weitere.length - 1 ? clip.fadeOut : 0,
        transitionIn: null,
        gainKeyframes: [],
      },
    });
  });
  return ops;
}
